import { Button } from "@/components/ui/button.tsx";
import type { ReactNode, Ref } from "react";
import { Link } from "wouter";

/**
 * A markdown app link that reads as a standalone call to action rather than a
 * word in a sentence. `resolveMessageLink` decides which links get this
 * treatment; this is only how web draws it.
 *
 * Still an anchor, not a `<button>`: the destination is a real route, so
 * cmd-click, open-in-new-tab, copy-link-address, and the screen reader
 * announcing "link" all keep working. Only the styling changes.
 *
 * Lives in its own file so the onboarding tour pill can wrap it without
 * importing the markdown renderer that renders the pill.
 */
export const AppButtonLink = ({
  appHref,
  children,
  ref,
}: {
  appHref: string;
  children: ReactNode;
  ref?: Ref<HTMLAnchorElement>;
}) => (
  // Block-level (`flex`, not the Button's inline-flex) so it always takes its
  // own line. The agent authors the link and its "own line" varies: a single
  // newline in markdown is a soft break inside the same paragraph, which would
  // otherwise render the button mid-sentence.
  // `no-underline` because the surrounding `prose` underlines every anchor,
  // which on a pill reads as a mistake rather than a link.
  <Button asChild className="mt-1 flex w-fit no-underline" data-statsig-name="markdown-action-link">
    <Link href={appHref} ref={ref}>
      {children}
    </Link>
  </Button>
);
